import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@design-system';

import { FormEditorLoaded, useNewForm } from '@/features/quote-and-apply-builder';

import { Copy, Upload, X } from 'lucide-react';

import { type FundingPolicyTypeDef } from '@shared';

const ADD_NEW_VALUE = '__add_new__';

export function NewFormPage() {
  const {
    formConfig,
    title,
    formType,
    selectedCarrierId,
    customCarrier,
    isCustomCarrier,
    selectedProduct,
    customProduct,
    isCustomProduct,
    email,
    importSource,
    importError,
    isCopyLoading,
    selectedPolicyTypes,
    policyTypeFilter,
    stateCode,
    saveError,
    fileInputRef,
    isSaving,
    carriers,
    forms,
    uniqueProducts,
    filteredPolicyTypes,
    isValid,
    dispatch,
    handleFileImport,
    handleCopyForm,
    handleCreate,
    handleCarrierChange,
    handleProductChange,
    handleCancel,
    handleBack,
  } = useNewForm();

  if (formConfig) {
    return (
      <FormEditorLoaded
        formConfig={formConfig}
        carrierId=""
        fundingPolicyTypeId={selectedPolicyTypes}
        stateCode={stateCode.trim() || undefined}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 pb-12 pt-6">
      <div className="mb-8 border-b pb-6">
        <h1 className="text-xl font-bold text-gray-950">Create New Form</h1>
      </div>

      <div className="space-y-6">
        <div className="space-y-6">
          <div className="grid gap-2">
            <Label htmlFor="form-title">
              Form Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="form-title"
              value={title}
              onChange={event => dispatch({ type: 'SET_TITLE', value: event.target.value })}
              placeholder="e.g., Lincoln MoneyGuard Application"
              autoFocus
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="form-type">
              Form Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formType}
              onValueChange={val =>
                dispatch({ type: 'SET_FORM_TYPE', value: val as PolicyFormType })
              }
            >
              <SelectTrigger
                id="form-type"
                className="w-full"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="application-form">Application Form</SelectItem>
                <SelectItem value="pre-underwriting-form">Pre-Underwriting Form</SelectItem>
                <SelectItem value="partner-pre-underwriting-form">
                  Partner Pre-Underwriting Form
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>
              Carrier <span className="text-destructive">*</span>
            </Label>
            {isCustomCarrier ? (
              <div className="flex gap-2">
                <Input
                  value={customCarrier}
                  onChange={event =>
                    dispatch({ type: 'SET_CUSTOM_CARRIER', value: event.target.value })
                  }
                  placeholder="Enter carrier name"
                  className="flex-1"
                />
                {carriers && carriers.length > 0 && (
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={() => dispatch({ type: 'SELECT_EXISTING_CARRIER' })}
                  >
                    Select existing
                  </Button>
                )}
              </div>
            ) : (
              <Select
                value={selectedCarrierId}
                onValueChange={handleCarrierChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a carrier" />
                </SelectTrigger>
                <SelectContent>
                  {carriers?.map(carrier => (
                    <SelectItem
                      key={carrier.insuranceCarrierId}
                      value={String(carrier.insuranceCarrierId)}
                    >
                      {carrier.carrierName}
                    </SelectItem>
                  ))}
                  <SelectItem value={ADD_NEW_VALUE}>+ Add new carrier</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid gap-2">
            <Label>
              Product <span className="text-destructive">*</span>
            </Label>
            {isCustomProduct || isCustomCarrier ? (
              <div className="flex gap-2">
                <Input
                  value={customProduct}
                  onChange={event =>
                    dispatch({ type: 'SET_CUSTOM_PRODUCT', value: event.target.value })
                  }
                  placeholder="Enter product name"
                  className="flex-1"
                />
                {!isCustomCarrier && uniqueProducts.length > 0 && (
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={() => dispatch({ type: 'SELECT_EXISTING_PRODUCT' })}
                  >
                    Select existing
                  </Button>
                )}
              </div>
            ) : (
              <Select
                value={selectedProduct}
                onValueChange={handleProductChange}
                disabled={!selectedCarrierId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={selectedCarrierId ? 'Select a product' : 'Select a carrier first'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {uniqueProducts.map(product => (
                    <SelectItem
                      key={product}
                      value={product}
                    >
                      {product}
                    </SelectItem>
                  ))}
                  <SelectItem value={ADD_NEW_VALUE}>+ Add new product</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={event => dispatch({ type: 'SET_EMAIL', value: event.target.value })}
              placeholder="e.g., submissions@carrier.com"
            />
          </div>
        </div>

        <Separator />

        <div className="space-y-3">
          <Label>Start From</Label>

          {importSource ? (
            <Alert
              variant="success"
              className="items-center p-2"
            >
              {importSource.startsWith('Copied') ? (
                <Copy className="ml-2.5 size-4" />
              ) : (
                <Upload className="ml-2.5 size-4" />
              )}
              <AlertTitle className="ml-2 flex items-center gap-2">
                <span className="flex-1 truncate">{importSource}</span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => dispatch({ type: 'CLEAR_IMPORT' })}
                >
                  <X className="size-4" />
                </Button>
              </AlertTitle>
            </Alert>
          ) : (
            <div className="flex flex-col gap-2">
              {selectedCarrierId && forms && forms.length > 0 && (
                <Select
                  onValueChange={handleCopyForm}
                  disabled={isCopyLoading}
                >
                  <SelectTrigger className="w-full flex-1">
                    <SelectValue
                      placeholder={isCopyLoading ? 'Loading...' : 'Copy existing form'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {forms.map(form => (
                      <SelectItem
                        key={form.id}
                        value={form.id}
                      >
                        {form.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                variant="outline"
                className="shrink-0 border-dashed"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="size-4" />
                Upload Json
              </Button>
            </div>
          )}

          {importError && (
            <Alert variant="destructive">
              <AlertDescription>{importError}</AlertDescription>
            </Alert>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleFileImport}
          />
        </div>

        <Separator />

        <div className="space-y-3">
          <Label>
            Policy Types <span className="text-destructive">*</span>
          </Label>
          <Input
            value={policyTypeFilter}
            onChange={event =>
              dispatch({ type: 'SET_POLICY_TYPE_FILTER', value: event.target.value })
            }
            placeholder="Filter policy types..."
          />
          <ScrollArea className="h-36 rounded-md border p-2">
            {filteredPolicyTypes.map((policyTypeDef: FundingPolicyTypeDef) => (
              <Label
                key={policyTypeDef.value}
                className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 font-normal"
              >
                <Checkbox
                  checked={selectedPolicyTypes.includes(policyTypeDef.value)}
                  onCheckedChange={() =>
                    dispatch({ type: 'TOGGLE_POLICY_TYPE', value: policyTypeDef.value })
                  }
                />
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium">{policyTypeDef.label}</span>
                  {policyTypeDef.carrier && (
                    <span className="text-muted-foreground ml-1.5 text-xs">
                      ({policyTypeDef.carrier})
                    </span>
                  )}
                </div>
              </Label>
            ))}
            {filteredPolicyTypes.length === 0 && (
              <p className="text-muted-foreground py-4 text-center text-sm">
                No matching policy types
              </p>
            )}
          </ScrollArea>
          {selectedPolicyTypes.length > 0 && (
            <p className="text-muted-foreground text-xs">{selectedPolicyTypes.length} selected</p>
          )}
        </div>

        <Separator />

        <div className="grid gap-2">
          <Label htmlFor="state-code">State Code</Label>
          <Input
            id="state-code"
            value={stateCode}
            onChange={event => dispatch({ type: 'SET_STATE_CODE', value: event.target.value })}
            placeholder="e.g., CA, NY, FL"
            className="w-full"
          />
        </div>

        {saveError && (
          <Alert variant="destructive">
            <AlertDescription>{saveError}</AlertDescription>
          </Alert>
        )}

        <Separator />

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!isValid || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Creating...' : 'Create Form'}
          </Button>
        </div>
      </div>
    </div>
  );
}
